import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Heart, Phone, ArrowLeft, ShieldCheck } from 'lucide-react';
import { auth as firebaseAuth } from '../lib/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { toast } from 'sonner';

export default function AuthPage() {
  const { t } = useLanguage();
  const { user, needsRegistration, authError, firebaseUser, retryVerification } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [otpSendError, setOtpSendError] = useState('');

  useEffect(() => {
    if (user) {
      console.log('[AuthPage] User found, navigating to dashboard');
      navigate('/dashboard');
    } else if (needsRegistration) {
      console.log('[AuthPage] Needs registration, navigating to register');
      navigate('/register');
    }
  }, [user, needsRegistration, navigate]);

  // Show auth error from backend verification
  useEffect(() => {
    if (authError && firebaseUser) {
      toast.error(`Profile verification failed: ${authError}. Please try again.`);
    }
  }, [authError, firebaseUser]);

  const setupRecaptcha = useCallback(() => {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch(e) {}
      window.recaptchaVerifier = null;
    }
    // Remove old recaptcha container content
    const container = document.getElementById('recaptcha-container');
    if (container) container.innerHTML = '';

    window.recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {
        console.log('[AuthPage] reCAPTCHA resolved');
      },
      'expired-callback': () => {
        console.log('[AuthPage] reCAPTCHA expired');
        window.recaptchaVerifier = null;
      }
    });
    return window.recaptchaVerifier;
  }, []);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setOtpSendError('');
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const verifier = setupRecaptcha();
      const fullPhone = `+91${cleanPhone}`;
      const result = await signInWithPhoneNumber(firebaseAuth, fullPhone, verifier);
      setConfirmResult(result);
      setStep('otp');
      toast.success(`${t('auth.otp_sent')} +91${cleanPhone}`);
    } catch (err) {
      console.error('OTP send error:', err.code, err.message);
      let errorMsg = '';
      if (err.code === 'auth/too-many-requests') {
        errorMsg = 'Too many attempts. Please try again after some time.';
      } else if (err.code === 'auth/invalid-phone-number') {
        errorMsg = 'Invalid phone number. Please check and try again.';
      } else if (err.code === 'auth/captcha-check-failed') {
        errorMsg = 'Captcha verification failed. Please refresh the page and try again.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMsg = 'Firebase authentication failed on this domain. Please ensure this domain is added to Firebase Console → Authentication → Settings → Authorized domains.';
      } else if (err.code === 'auth/quota-exceeded') {
        errorMsg = 'SMS quota exceeded. Please try again later.';
      } else if (err.code === 'auth/unauthorized-domain') {
        errorMsg = 'This domain is not authorized for Firebase authentication. Please contact support.';
      } else {
        errorMsg = err.message || t('common.error');
      }
      setOtpSendError(errorMsg);
      toast.error(errorMsg);
      window.recaptchaVerifier = null;
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const result = await confirmResult.confirm(otp);
      console.log('[AuthPage] OTP verified, Firebase user:', result.user?.uid);
      toast.success('OTP verified! Checking your profile...');
      // onAuthStateChanged in AuthContext will handle the rest
    } catch (err) {
      console.error('OTP verify error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        toast.error('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        toast.error('OTP expired. Please request a new one.');
        setStep('phone');
        setOtp('');
      } else {
        toast.error(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <Card className="w-full max-w-md p-8 rounded-2xl border border-[#E7E5E4] shadow-lg bg-white">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#D9381E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-[#D9381E] fill-[#D9381E]" />
          </div>
          <h1 className="text-2xl font-bold text-[#2D2424]">{t('auth.title')}</h1>
          <p className="mt-2 text-sm text-[#78716C]">{t('auth.subtitle')}</p>
        </div>

        {/* Show auth error with retry when backend verification fails */}
        {authError && firebaseUser && step === 'phone' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="auth-error-banner">
            <p className="text-sm text-red-700 mb-2">Profile verification failed. This may be a temporary issue.</p>
            <Button
              onClick={retryVerification}
              variant="outline"
              size="sm"
              className="text-[#D9381E] border-[#D9381E] hover:bg-[#D9381E]/10"
              data-testid="retry-verification-btn"
            >
              Retry Verification
            </Button>
          </div>
        )}

        {step === 'phone' ? (
          <form onSubmit={handleSendOTP} className="space-y-5">
            <div>
              <Label className="text-sm font-medium text-[#2D2424]">{t('auth.phone_label')}</Label>
              <div className="mt-2 flex gap-2">
                <div className="flex items-center px-3 bg-[#F9F5F0] rounded-lg border border-[#E7E5E4] text-sm font-medium text-[#2D2424]">
                  +91
                </div>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder={t('auth.phone_placeholder')}
                  className="h-12 rounded-lg border-[#E7E5E4] focus:border-[#D9381E] focus:ring-[#D9381E]/20"
                  data-testid="phone-input"
                  maxLength={10}
                  required
                />
              </div>
            </div>
            {otpSendError && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="otp-send-error">{otpSendError}</p>
            )}
            <Button
              type="submit"
              disabled={loading || phone.length !== 10}
              className="w-full bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full py-6 text-base font-semibold shadow-md disabled:opacity-50"
              data-testid="send-otp-btn"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t('auth.sending')}</>
              ) : (
                <><Phone className="w-4 h-4 mr-2" /> {t('auth.send_otp')}</>
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-5">
            <button
              type="button"
              onClick={() => { setStep('phone'); setOtp(''); window.recaptchaVerifier = null; }}
              className="flex items-center gap-1 text-sm text-[#D9381E] hover:underline mb-2"
              data-testid="change-number-btn"
            >
              <ArrowLeft className="w-4 h-4" /> {t('auth.change_number')}
            </button>
            <div className="text-center">
              <p className="text-sm text-[#78716C]">{t('auth.otp_sent')} <span className="font-semibold text-[#2D2424]">+91{phone}</span></p>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#2D2424]">{t('auth.otp_label')}</Label>
              <div className="mt-3 flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full py-6 text-base font-semibold shadow-md disabled:opacity-50"
              data-testid="verify-otp-btn"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> {t('auth.verifying')}</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" /> {t('auth.verify')}</>
              )}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-[#78716C]">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </Card>
      <div id="recaptcha-container" />
    </div>
  );
}
